import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showAlert } from 'state/actions/site';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import SidebarPlaceholder from 'tailwind-components/ContentWithSidebar/SidebarPlaceholder';
import AnswerDetails from './AnswerDetails';
import useNotifyTourPageReady from 'tailwind-components/PortalWalkthrough/useNotifyTourPageReady';

export default function FAQs({
  getFAQsData,
  setLocalLoader,
  faqs,
  disableUrlNav
}) {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openAccordionKey, setOpenAccordionKey] = useState(null);
  // local loading state so the FAQs section can show its own skeleton (the
  // parent loader is not always wired, e.g. inside the Resources tabs)
  const [isLoading, setIsLoading] = useState(true);
  const cancelTokenSource = axios.CancelToken.source();

  const { faqId, questionNumber } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useNotifyTourPageReady(isLoading);

  useEffect(() => {
    if (disableUrlNav || !faqs || faqs.length === 0) return;

    if (faqId && questionNumber) {
      const faq = faqs.find(f => String(f.id) === String(faqId));
      const index = Number.parseInt(questionNumber, 10) - 1;

      if (
        !faq ||
        Number.isNaN(index) ||
        index < 0 ||
        index >= faq.questions.length
      ) {
        dispatch(
          showAlert({
            message: 'FAQ not found',
            code: 'error'
          })
        );
        navigate('/faqs');
        return;
      }

      setSelectedQuestion(faq.questions[index]);
      setOpenAccordionKey(faq.type);
      setSidebarOpen(true);
    }
  }, [faqs, faqId, questionNumber, navigate, dispatch]);

  useEffect(() => {
    getFAQsData(value => {
      setLocalLoader(value);
      setIsLoading(value);
    }, cancelTokenSource);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [getFAQsData, setLocalLoader]);

  const handleQuestionClick = (question, parentFaq) => {
    setSelectedQuestion(question);

    if (!disableUrlNav) {
      const questionIndex = parentFaq.questions.indexOf(question) + 1;
      navigate(`/faqs/${parentFaq.id}/${questionIndex}`);
    }

    setSidebarOpen(true);
  };

  const handleBackClick = () => {
    setSelectedQuestion(null);
    if (!disableUrlNav) navigate('/faqs');
    setSidebarOpen(false);
  };

  const mainContent = (
    <div className='flex flex-col pl-4 pr-1 pb-2 text-sm h-full min-h-0 rounded-[1.5%] bg-white'>
      <h2 className='sticky top-0 z-10 bg-white pt-4 pb-3 text-center text-2xl font-bold text-gray-900'>
        Frequently Asked Questions
      </h2>
      <div data-tour='faqs-list' className='overflow-y-auto flex-1 min-h-0 show-scrollbar'>
        {isLoading ? (
          <div className='flex h-full flex-col items-center justify-center gap-3 py-10'>
            <Loader2
              aria-hidden
              className='size-8 shrink-0 animate-spin text-primary'
            />
            <p className='m-0 text-base font-medium text-gray-800'>
              Loading FAQs...
            </p>
          </div>
        ) : (
          faqs.map(faq => (
            <FAQAccordion
              key={faq.type}
              title={faq.type}
              questions={faq.questions}
              onClick={q => handleQuestionClick(q, faq)}
              selectedQuestion={selectedQuestion}
              isOpen={openAccordionKey === faq.type}
              onToggle={() =>
                setOpenAccordionKey(prev =>
                  prev === faq.type ? null : faq.type
                )
              }
            />
          ))
        )}
      </div>
    </div>
  );

  const sidebarContent = selectedQuestion ? (
    <AnswerDetails question={selectedQuestion} />
  ) : (
    <SidebarPlaceholder text='Select a question to see the answer details' />
  );

  return (
    <div className='w-full h-full'>
      <ContentWithSidebar
        mainContent={mainContent}
        sidebarContent={sidebarContent}
        sidebarAsSheetOnMobile
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={setSidebarOpen}
        sidebarTitle='Answer Details'
        handleCloseSidebar={handleBackClick}
        desktopHeaderClassName='bg-white rounded-t-xl'
        desktopSidebarGridClassName='lg:grid-cols-[minmax(0,1fr)_400px]'
      />
    </div>
  );
}

FAQs.propTypes = {
  getFAQsData: PropTypes.func.isRequired,
  setLocalLoader: PropTypes.func.isRequired,
  faqs: PropTypes.array,
  disableUrlNav: PropTypes.bool
};
